"use client";

import DOMPurify from "dompurify";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, type MouseEvent } from "react";
import { Carousel } from "react-responsive-carousel";
import { Avatar } from "../avatar";
import { LoadingContainer } from "~/app/[course]/loader";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";

const DEFAULT_BANNER_PHOTO = "/defaults/default-banner.webp";

const renderArrow = (
  direction: "prev" | "next",
  clickHandler: () => void,
  hasArrow: boolean,
) => {
  if (!hasArrow) return null;
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const positionClass = direction === "prev" ? "left-3" : "right-3";
  const label = direction === "prev" ? "Previous image" : "Next image";
  // Stop propagation so the carousel's onClickItem (course navigation)
  // does not fire when the user taps an arrow.
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    clickHandler();
  };
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`absolute top-1/2 ${positionClass} z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-md transition-colors hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white`}
    >
      <Icon className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
};

export const Course = ({
  images,
  courseName,
  location,
  description,
  courseId,
  logo,
}: {
  images: string[];
  courseName: string;
  location: string;
  description: string;
  courseId: string;
  logo?: string;
}) => {
  const router = useRouter();
  const courseImages = images.length > 0 ? images : [DEFAULT_BANNER_PHOTO];
  const href = `/${courseId}`;
  const hasMultipleImages = courseImages.length > 1;
  // The course page does a blocking, non-Suspense data fetch on the server,
  // so navigating there can take a couple seconds — show a full-screen loader
  // immediately on click instead of leaving the user staring at nothing.
  const [isNavigating, setIsNavigating] = useState(false);

  const bookNow = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    googleAnalyticsEvent({
      action: "btn_home_book_now",
      params: {
        content_type: "course",
        item_id: courseId,
        item_name: courseName,
      },
    });
    setIsNavigating(true);
    router.push(href);
  };

  const markNavigating = () => setIsNavigating(true);

  const renderImage = (src: string) => (
    <Image
      draggable={false}
      src={src}
      width={440}
      height={288}
      alt={courseName}
      priority
      className="object-cover w-full h-[288px]"
      unoptimized
    />
  );

  return (
    <div
      className="block bg-white rounded-xl shadow-lg overflow-hidden"
      data-testid="course-id"
      data-test={courseId}
      data-qa={courseName}
    >
      <LoadingContainer isLoading={isNavigating}>
        <div></div>
      </LoadingContainer>
      {hasMultipleImages ? (
        // Slide clicks are wired via onClickItem because react-responsive-carousel's
        // swipe tracking suppresses the default click on a <Link> wrapping a slide.
        <Carousel
          autoPlay
          showThumbs={false}
          showIndicators
          showArrows
          showStatus={false}
          swipeable
          infiniteLoop
          interval={3000}
          stopOnHover
          onClickItem={() => {
            markNavigating();
            router.push(href);
          }}
          renderArrowPrev={(clickHandler, hasPrev) =>
            renderArrow("prev", clickHandler, hasPrev)
          }
          renderArrowNext={(clickHandler, hasNext) =>
            renderArrow("next", clickHandler, hasNext)
          }
        >
          {courseImages.map((img, idx) => (
            <div key={idx} className="cursor-pointer">
              {renderImage(img)}
            </div>
          ))}
        </Carousel>
      ) : (
        <Link href={href} prefetch={false} onClick={markNavigating}>
          {renderImage(courseImages[0]!)}
        </Link>
      )}
      <Link href={href} prefetch={false} className="block" onClick={markNavigating}>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Avatar src={logo} isRounded={false} className="flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold truncate">{courseName}</h3>
              <p className="text-gray-500 text-xs truncate">{location}</p>
            </div>
          </div>
          <div className="mt-3 text-center">
            <button
              // Facility page is outside course context, so no course is synced
              // → this sends to the master property only; the clicked course is
              // identified via the item_id / item_name params.
              onClick={bookNow}
              className="bg-black text-white text-sm font-semibold px-4 py-2 rounded-xl border-2 border-white"
            >
              Book Now
            </button>
          </div>
          <div
            className="text-justify text-gray-700 text-sm mt-4 [&>p]:mb-2 [&>p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
          />
        </div>
      </Link>
    </div>
  );
};
